<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.inventory'),
                        href: meta.links.inventory_index,
                    },
                    {
                        name: $tc('messages.gamification_trades', 1),
                        href: meta.links.index,
                    },
                    {
                        name: $t('messages.create'),
                        href: null,
                    },
                ]"
            />
        </template>

        <div v-if="form.second_user_id === null">
            <div>
                {{ $t('messages.who_do_you_want_to_trade_with?') }}

                <FormInput v-model="search" :placeholder="$t('messages.search')" />
            </div>

            <transition-group
                class="md:grid md:grid-cols-4 md:gap-6 bg-gray-50 p-4"
                enter-active-class="transition-opacity ease-linear duration-300"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-opacity ease-linear duration-300"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
                tag="span"
            >
                <CardPanel v-for="otherStudent in filteredStudents" :key="otherStudent.id" class="mt-1">
                    <div class="flex justify-between space-x-2">
                        <div class="flex space-x-2">
                            <img
                                class="inline-block h-8 w-8 rounded-full ring-2 ring-white"
                                :src="otherStudent.avatar_or_profile_photo"
                                alt="avatar"
                            />

                            <div class="text-sm">
                                {{ otherStudent.first_name_and_initial }}
                            </div>
                        </div>

                        <div>
                            <button
                                type="button"
                                @click="selectStudent(otherStudent)"
                                class="text-indigo-700 bg-indigo-100 hover:bg-indigo-200 inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="w-4 h-4"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    stroke-width="2"
                                    stroke="currentColor"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                    <line x1="3" y1="12" x2="6" y2="12"></line>
                                    <line x1="12" y1="3" x2="12" y2="6"></line>
                                    <line x1="7.8" y1="7.8" x2="5.6" y2="5.6"></line>
                                    <line x1="16.2" y1="7.8" x2="18.4" y2="5.6"></line>
                                    <line x1="7.8" y1="16.2" x2="5.6" y2="18.4"></line>
                                    <path d="M12 12l9 3l-4 2l-2 4l-3 -9"></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </CardPanel>
            </transition-group>
        </div>

        <div v-else>
            <form action="" @submit.prevent="submit">
                <div class="md:grid md:grid-cols-2 md:gap-6">
                    <div>
                        {{ $t('messages.you_give') }}:

                        <div
                            class="bg-gray-50 rounded rounded-lg p-4 flex flex-col justify-between"
                            style="min-height: 200px"
                        >
                            <svg
                                v-if="givenItems.length === 0"
                                class="mx-auto h-12 w-12 text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    vector-effect="non-scaling-stroke"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                                />
                            </svg>
                            <div v-else class="flex flex-wrap space-x-2">
                                <div
                                    v-for="item in givenItems"
                                    :key="`${item.id}_${item[trade.current]}`"
                                    class="relative w-20 h-20"
                                >
                                    <img :src="item.image_path" alt="" class="h-20 absolute" />
                                    <BadgePill
                                        :color="item[trade.current] ? 'indigo' : 'gray'"
                                        class="absolute bottom-0 right-0"
                                    >
                                        &times;{{ item[trade.current] }}
                                    </BadgePill>
                                </div>
                            </div>

                            <div>
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <img
                                            class="h-6 w-6"
                                            src="/assets/gamification/currency/coin.svg"
                                            alt="emerald"
                                        />
                                    </div>
                                    <input
                                        type="number"
                                        v-model.number="form.coins[trade.current]"
                                        step="1"
                                        min="0"
                                        class="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                                        :placeholder="$t('messages.coins_amount')"
                                    />
                                </div>
                                <FormError :value="form.errors[`coins.${trade.current}`]" class="mt-2" />
                            </div>
                        </div>
                    </div>

                    <div>
                        {{ $t('messages.you_receive') }}:

                        <div
                            class="bg-gray-50 rounded rounded-lg p-4 flex flex-col justify-between"
                            style="min-height: 200px"
                        >
                            <svg
                                v-if="requestedItems.length === 0"
                                class="mx-auto h-12 w-12 text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    vector-effect="non-scaling-stroke"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                                />
                            </svg>
                            <div v-else class="flex flex-wrap space-x-2">
                                <div
                                    v-for="item in requestedItems"
                                    :key="`${item.id}_${item[trade.other]}`"
                                    class="relative w-20 h-20"
                                >
                                    <img :src="item.image_path" alt="" class="h-20 absolute" />
                                    <BadgePill
                                        :color="item[trade.other] ? 'indigo' : 'gray'"
                                        class="absolute bottom-0 right-0"
                                    >
                                        &times;{{ item[trade.other] }}
                                    </BadgePill>
                                </div>
                            </div>

                            <div>
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <img
                                            class="h-6 w-6"
                                            src="/assets/gamification/currency/coin.svg"
                                            alt="emerald"
                                        />
                                    </div>
                                    <input
                                        type="number"
                                        v-model.number="form.coins[trade.other]"
                                        step="1"
                                        min="0"
                                        class="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                                        :placeholder="$t('messages.coins_amount')"
                                    />
                                </div>
                                <FormError :value="form.errors[`coins.${trade.other}`]" class="mt-2" />
                            </div>
                        </div>
                    </div>
                </div>

                <div>
                    <transition
                        enter-active-class="transition ease-out duration-100"
                        enter-from-class="transition ease-out duration-100"
                        enter-to-class="transform opacity-100 scale-100"
                        leave-active-class="transition ease-in duration-75"
                        leave-from-class="transform opacity-100 scale-100"
                        leave-to-class="transform opacity-0 scale-95"
                    >
                        <ProgressBar :percent="percent" v-if="percent !== null" :show-text="false" color="gray" />
                    </transition>
                </div>

                <div>{{ givenItemsPrice }} / {{ requestedItemsPrice }}</div>

                <div class="mt-3">
                    <AppButton desired-action element="button" type="submit">
                        {{ $t('messages.continue') }}
                    </AppButton>
                </div>

                <EmptyState picture v-if="items.length === 0">
                    <template v-slot:picture />
                </EmptyState>

                <Tabs class="mt-5" v-else>
                    <Tab :title="$tc('messages.gamification_items', 2)">
                        <div class="mt-5">
                            <div class="md:grid md:grid-cols-3 lg:grid-cols-6 md:gap-6">
                                <div v-for="item in items" :key="`${item.id}_${item.count}`">
                                    <img :src="item.image_path" alt="" />
                                    <div>
                                        {{ item.name }}
                                    </div>
                                    <div class="text-gray-600 text-sm">
                                        {{ item.options_description }}
                                    </div>
                                    <div class="text-gray-600 text-sm" v-if="item.description">
                                        <details class="mt-3 sm:col-span-12">
                                            <summary>
                                                <svg
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    class="w-6 h-6 text-gray-400"
                                                >
                                                    <path
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    ></path>
                                                </svg>
                                            </summary>
                                            {{ item.description }}
                                        </details>
                                    </div>
                                    <div class="flex justify-between items-center mt-1">
                                        <div>
                                            <BadgePill :color="item.count ? 'indigo' : 'gray'">
                                                &times;{{ item.count }}
                                            </BadgePill>
                                        </div>
                                    </div>
                                    <div
                                        class="flex justify-between items-center mt-1"
                                        v-if="item.required_level_description"
                                    >
                                        {{ $t('messages.level_required') }}:
                                        {{ item.required_level_description }}
                                    </div>

                                    <div>
                                        <div class="flex justify-between py-2">
                                            <div>
                                                {{ $t('messages.request') }}
                                                <span v-if="item[trade.other]">({{ item[trade.other] }})</span>
                                            </div>

                                            <div class="flex justify-between space-x-2">
                                                <button
                                                    type="button"
                                                    @click="decreaseRequestItem(item)"
                                                    :class="item[trade.other] > 0 ? 'text-indigo-500' : 'text-gray-300'"
                                                >
                                                    <svg
                                                        class="w-6 h-6"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                                                            clip-rule="evenodd"
                                                        ></path>
                                                    </svg>
                                                </button>

                                                <button type="button" @click="increaseRequestItem(item)">
                                                    <svg
                                                        class="w-6 h-6 text-indigo-500"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                                                            clip-rule="evenodd"
                                                        ></path>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="item.count">
                                        <div class="flex justify-between py-2">
                                            <div>
                                                {{ $t('messages.give') }}
                                                <span v-if="item[trade.current]"
                                                    >({{ item[trade.current] }} / {{ item.count }})</span
                                                >
                                            </div>

                                            <div class="flex justify-between space-x-2">
                                                <button
                                                    type="button"
                                                    @click="decreaseGivenItem(item)"
                                                    :class="
                                                        item[trade.current] > 0 ? 'text-indigo-500' : 'text-gray-300'
                                                    "
                                                >
                                                    <svg
                                                        class="w-6 h-6"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                                                            clip-rule="evenodd"
                                                        ></path>
                                                    </svg>
                                                </button>

                                                <button
                                                    type="button"
                                                    @click="increaseGivenItem(item)"
                                                    :class="
                                                        item[trade.current] < item.count
                                                            ? 'text-indigo-500'
                                                            : 'text-gray-300'
                                                    "
                                                >
                                                    <svg
                                                        class="w-6 h-6"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                                                            clip-rule="evenodd"
                                                        ></path>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tab>
                    <Tab :title="$tc('messages.card_collections', 2)">
                        <EmptyState picture v-if="cardCollections.length === 0">
                            <template v-slot:picture />
                        </EmptyState>

                        <div class="bg-gray-50 p-2 mt-3" v-else>
                            <div
                                class="col-span-1 bg-white rounded-lg shadow divide-y divide-gray-200"
                                v-for="cardCollection in cardCollections"
                                :key="cardCollection.id"
                            >
                                <div class="w-full flex items-center justify-between p-6 space-x-6">
                                    <div class="flex-1 truncate">
                                        <div class="flex items-center space-x-3 justify-between">
                                            <h3 class="text-gray-900 text-sm font-medium truncate">
                                                {{ cardCollection.name }}
                                            </h3>
                                        </div>
                                        <div class="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-6 mt-1">
                                            <div
                                                v-for="card in cardCollection.cards"
                                                class=""
                                                :title="card.name"
                                                :key="card.id"
                                            >
                                                <div class="w-full relative aspect-w-1 aspect-h-1">
                                                    <div
                                                        class="w-full h-full absolute bg-no-repeat bg-center bg-contain"
                                                        :class="{
                                                            'filter saturate-0': !card.count,
                                                        }"
                                                        :style="`background-image: url(${card.image_path});`"
                                                    />
                                                </div>
                                                <div class="flex items-center justify-between mt-1">
                                                    <CardRarityIndicator :rarity="card.rarity" />
                                                    <BadgePill :color="card.count ? 'indigo' : 'gray'">
                                                        &times;{{ card.count }}
                                                    </BadgePill>
                                                </div>
                                                <div class="flex items-center justify-between mt-1">
                                                    {{ card.name }}
                                                </div>
                                                <div>
                                                    <div class="flex justify-between py-2">
                                                        <div>
                                                            {{ $t('messages.request') }}
                                                            <span v-if="card[trade.other]"
                                                                >({{ card[trade.other] }})</span
                                                            >
                                                        </div>

                                                        <div class="flex justify-between space-x-2">
                                                            <button
                                                                type="button"
                                                                @click="decreaseRequestCard(card)"
                                                                :class="
                                                                    card[trade.other] > 0
                                                                        ? 'text-indigo-500'
                                                                        : 'text-gray-300'
                                                                "
                                                            >
                                                                <svg
                                                                    class="w-6 h-6"
                                                                    fill="currentColor"
                                                                    viewBox="0 0 20 20"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        fill-rule="evenodd"
                                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                                                                        clip-rule="evenodd"
                                                                    ></path>
                                                                </svg>
                                                            </button>

                                                            <button type="button" @click="increaseRequestCard(card)">
                                                                <svg
                                                                    class="w-6 h-6 text-indigo-500"
                                                                    fill="currentColor"
                                                                    viewBox="0 0 20 20"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        fill-rule="evenodd"
                                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                                                                        clip-rule="evenodd"
                                                                    ></path>
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div>
                                                    <div class="flex justify-between py-2">
                                                        <div>
                                                            {{ $t('messages.give') }}
                                                            <span v-if="card[trade.current]"
                                                                >({{ card[trade.current] }}
                                                                /
                                                                {{ card.count }})</span
                                                            >
                                                        </div>

                                                        <div class="flex justify-between space-x-2">
                                                            <button
                                                                type="button"
                                                                @click="decreaseGivenCard(card)"
                                                                :class="
                                                                    card[trade.current] > 0
                                                                        ? 'text-indigo-500'
                                                                        : 'text-gray-300'
                                                                "
                                                            >
                                                                <svg
                                                                    class="w-6 h-6"
                                                                    fill="currentColor"
                                                                    viewBox="0 0 20 20"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        fill-rule="evenodd"
                                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                                                                        clip-rule="evenodd"
                                                                    ></path>
                                                                </svg>
                                                            </button>

                                                            <button
                                                                type="button"
                                                                @click="increaseGivenCard(card)"
                                                                :class="
                                                                    card[trade.current] < card.count
                                                                        ? 'text-indigo-500'
                                                                        : 'text-gray-300'
                                                                "
                                                            >
                                                                <svg
                                                                    class="w-6 h-6"
                                                                    fill="currentColor"
                                                                    viewBox="0 0 20 20"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        fill-rule="evenodd"
                                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                                                                        clip-rule="evenodd"
                                                                    ></path>
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tab>
                </Tabs>
            </form>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import BadgePill from '@/components/BadgePill.vue';
import CardPanel from '@/Shared/CardPanel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import deburr from 'lodash/deburr';
import Tabs from '@/components/Tabs.vue';
import Tab from '@/components/Tab.vue';
import CardRarityIndicator from '@/components/CardRarityIndicator.vue';
import FormError from '@/components/Forms/FormError.vue';
import ProgressBar from '@/components/Gamification/ProgressBar.vue';

export default {
    components: {
        ProgressBar,
        FormError,
        EmptyState,
        PanelPage,
        AppButton,
        BadgePill,
        CardPanel,
        FormInput,
        Tabs,
        Tab,
        CardRarityIndicator,
    },
    layout: Layout,
    name: 'Create',
    props: ['meta', 'items', 'students', 'cardCollections', 'trade'],
    data() {
        return {
            search: '',
            form: this.$inertia.form({
                second_user_id: null,
                coins: {
                    first: null,
                    second: null,
                },
            }),
        };
    },
    methods: {
        submit() {
            this.form
                .transform((data) => {
                    data.items = this.items
                        .filter((i) => i.first + i.second > 0)
                        .map((i) => {
                            return {
                                id: i.id,
                                first: i.first,
                                second: i.second,
                            };
                        });

                    data.cards = this.cardCollections
                        .flatMap((c) => c.cards)
                        .filter((i) => i.first + i.second > 0)
                        .map((i) => {
                            return {
                                id: i.id,
                                first: i.first,
                                second: i.second,
                            };
                        });

                    return data;
                })
                .post(this.meta.links.store);
        },
        increaseRequestItem(item) {
            if (item[this.trade.other] < 15) {
                item[this.trade.other]++;
            }
        },
        decreaseRequestItem(item) {
            if (item[this.trade.other] > 0) {
                item[this.trade.other]--;
            }
        },
        increaseGivenItem(item) {
            if (item[this.trade.current] < item.count) {
                item[this.trade.current]++;
            }
        },
        decreaseGivenItem(item) {
            if (item[this.trade.current] > 0) {
                item[this.trade.current]--;
            }
        },
        increaseRequestCard(card) {
            if (card[this.trade.other] < 15) {
                card[this.trade.other]++;
            }
        },
        decreaseRequestCard(card) {
            if (card[this.trade.other] > 0) {
                card[this.trade.other]--;
            }
        },
        increaseGivenCard(card) {
            if (card[this.trade.current] < card.count) {
                card[this.trade.current]++;
            }
        },
        decreaseGivenCard(card) {
            if (card[this.trade.current] > 0) {
                card[this.trade.current]--;
            }
        },
        selectStudent(student) {
            this.form.second_user_id = student.id;
        },
    },
    computed: {
        givenItems: function () {
            let cards = this.cardCollections.flatMap((c) => c.cards).filter((i) => i[this.trade.current]);
            let items = this.items.filter((i) => i[this.trade.current]);
            return [...items, ...cards];
        },
        requestedItems: function () {
            let cards = this.cardCollections.flatMap((c) => c.cards).filter((i) => i[this.trade.other]);
            let items = this.items.filter((i) => i[this.trade.other]);
            return [...items, ...cards];
        },
        givenItemsPrice: function () {
            return (
                this.givenItems.map((i) => i.price * i[this.trade.current]).reduce((a, b) => a + b, 0) +
                this.form.coins[this.trade.current]
            );
        },
        requestedItemsPrice: function () {
            return (
                this.requestedItems.map((i) => i.price * i[this.trade.other]).reduce((a, b) => a + b, 0) +
                this.form.coins[this.trade.other]
            );
        },
        percent: function () {
            const total = this.givenItemsPrice + this.requestedItemsPrice;

            if (total === 0) {
                return null;
            }

            return Math.round((this.givenItemsPrice / total) * 100);
        },
        filteredStudents: function () {
            if (this.search.length < 2) {
                return [];
            }
            return this.students.filter((s) => {
                const fullName = deburr(s.full_name.toUpperCase());
                const search = deburr(this.search.trim().toUpperCase());

                return fullName.includes(search);
            });
        },
    },
};
</script>

<style scoped>
details summary::-webkit-details-marker,
details summary::marker {
    display: none;
    content: '';
}
</style>
